import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api, { apiError, fileUrl } from "@/lib/api";
import { ShieldCheck, SpinnerGap, XCircle, Keyboard } from "@phosphor-icons/react";

const KIOSK_ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789"; // must match backend KIOSK_ALPHABET

const format = (raw) => {
  const clean = (raw || "").toUpperCase().split("").filter((c) => KIOSK_ALPHABET.includes(c)).join("").slice(0, 12);
  return clean.replace(/(.{4})/g, "$1-").replace(/-$/, "");
};

export default function Kiosk() {
  const { id } = useParams();
  const nav = useNavigate();
  const [info, setInfo] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/public/kiosk/${id}`).then(({ data }) => setInfo(data)).catch(() => setInfo({ enabled: false }));
  }, [id]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const { data } = await api.post(`/public/kiosk/${id}/redeem`, { code });
      nav(`/vote/${data.token}?kiosk=${id}`, { replace: true });
    } catch (e) {
      setErr(apiError(e.response?.data?.detail) || "That code didn't work. Please try again.");
      setCode("");
    } finally { setBusy(false); }
  };

  if (info === null) return <div className="min-h-[100dvh] flex items-center justify-center text-zinc-400 font-mono text-sm">loading…</div>;

  const brand = info.org?.branding?.primary_color || "#1F6FE5";

  if (!info.enabled || info.status !== "live") {
    return (
      <div className="min-h-[100dvh] bg-[#0B1220] flex items-center justify-center p-8">
        <div className="text-center text-white/80">
          <XCircle size={64} weight="fill" className="mx-auto mb-5 text-white/40" />
          <h1 className="font-display font-bold text-3xl tracking-tight mb-2">Kiosk unavailable</h1>
          <p className="text-white/50">{info.status && info.status !== "live" ? "Voting is not currently open." : "Kiosk voting is not enabled for this election."}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-[#0B1220] text-white flex flex-col grain">
      <header className="border-b border-white/10">
        <div className="max-w-xl mx-auto px-6 h-16 flex items-center gap-3">
          {info.org?.branding?.logo_url
            ? <img src={fileUrl(info.org.branding.logo_url)} alt="" className="h-8 w-auto" />
            : <div className="w-7 h-7 rounded-[5px]" style={{ background: brand }} />}
          <span className="font-semibold">{info.org?.name || "SelectFlow"}</span>
          <span className="ml-auto inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-white/40"><ShieldCheck size={13} weight="fill" style={{ color: brand }} /> Kiosk</span>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center px-6">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md text-center">
          <Keyboard size={52} weight="duotone" className="mx-auto mb-5" style={{ color: brand }} />
          <h1 className="font-display font-bold text-4xl tracking-tight mb-2">Enter your code</h1>
          <p className="text-white/50 mb-8">{info.title} — type the code from your printed slip to cast your ballot.</p>
          {err && <div data-testid="kiosk-error" className="mb-5 text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3">{err}</div>}
          <form onSubmit={submit}>
            <input
              data-testid="kiosk-code-input"
              autoFocus
              value={code}
              onChange={(e) => setCode(format(e.target.value))}
              placeholder="XXXX-XXXX-XXXX"
              inputMode="text"
              autoComplete="off"
              className="w-full text-center font-mono text-3xl tracking-[0.25em] uppercase bg-white/5 border-2 border-white/15 focus:border-white/40 rounded-2xl px-4 py-6 outline-none transition-colors placeholder:text-white/20"
            />
            <button
              data-testid="kiosk-submit"
              disabled={busy || code.replace(/-/g, "").length < 12}
              className="mt-6 w-full h-14 rounded-2xl font-semibold text-lg flex items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-40"
              style={{ background: brand }}
            >
              {busy && <SpinnerGap size={22} className="animate-spin" />} Continue to ballot
            </button>
          </form>
          <p className="mt-8 text-xs text-white/30">Your ballot is anonymous. Codes are single-use.</p>
        </motion.div>
      </div>
    </div>
  );
}
